import React, { useState } from "react";
import { Button, Card, Col, FloatingLabel, Form, Row, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { deleteGuardian, deleteMember, postNote, patchGuardian, patchMember, patchCase } from "../../../../services/httpClient";
import DatePicker from "../../../ui/DatePicker";
import SelectObject from "../../../ui/SelectObject";

interface DeleteState {
  userId: string | undefined;
  setIsDeleting: any;
  userToDelete: string | undefined;
  setUserToDelete: any;
  fetchHousehold: any;
  resetHouseholdTab: any;
  type: string;
  caseStatus: string | undefined;
  family: any[];
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ConfirmDelete = ({
  userId,
  setIsDeleting,
  userToDelete,
  setUserToDelete,
  fetchHousehold,
  resetHouseholdTab,
  type,
  caseStatus,
  family,
  setShowToast,
  setToastText,
  setToastBody,
}: DeleteState) => {
  const { id } = useParams();
  const [exitDate, setExitDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [exitNote, setExitNote] = useState<string>();
  const [exitType, setExitType] = useState<string>("voluntary");
  const [newMainContact, setNewMainContact] = useState<string>("");
  const isPrimary = family.find((g: any) => g.id === userId)?.is_primary;
  const reassignMainContact = !!(
    isPrimary && (family?.filter((g: any) => g.id !== userId && g.role === "family" && !g.deleted_at).length || 0) > 0
  );

  const mainContactOptions = family
    .filter((g: any) => g.id !== userId && g.role === "family" && !g.deleted_at && !g.is_banned)
    .map((g: any) => ({
      id: g.id,
      key: g.id,
      name: `${g.firstname} ${g.lastname}`,
    }));

  const totalActiveUsers = family.filter((f: any) => !f.deleted_at).length;
  const isLastUser = totalActiveUsers === 1;

  var exitTypes: any[] = [
    { id: 0, key: "voluntary", name: "Voluntary" },
    { id: 1, key: "involuntary", name: "Involuntary" },
  ];

  const onClickDeleteFinalWExit = () => {
    setIsDeleting(false);
    setUserToDelete("");

    const deleteUserAndSaveNote = () => {
      if (type === "Guardian") {
        if (caseStatus === "active") {
          const note = {
            notetype: "exit",
            subject: {
              id: userId,
              type: "guardian",
            },
            fields: {
              exit_type: exitType,
              description: exitNote ?? "",
              date: new Date().toISOString().split("T")[0],
            },
          };

          // save exit note
          postNote(note, id)
            .then((res: any) => {
              const noteId = res[0].data.note_id;

              const exitData = {
                exit_at: exitDate,
                exit_note_id: noteId,
              };

              deleteGuardian(id, userId, exitData)
                .then((res: any) => {
                  resetHouseholdTab();
                  fetchHousehold();
                  setShowToast(true);
                  setToastText("Guardian Deleted");
                  setToastBody(userToDelete);
                })
                .catch((err: any) => {
                  console.log(err);
                });
            })
            .catch((err: any) => {
              console.log(err);
            });
        }
      } else if (type === "Member") {
        if (caseStatus === "active") {
          const note = {
            notetype: "exit",
            subject: {
              id: userId,
              type: "member",
            },
            fields: {
              exit_type: exitType,
              description: exitNote ?? "",
              date: new Date().toISOString().split("T")[0],
            },
          };

          // save exit note
          postNote(note, id)
            .then((res: any) => {
              const noteId = res[0].data.note_id;

              const exitData = {
                exit_at: exitDate,
                exit_note_id: noteId,
              };

              deleteMember(id, userId, exitData)
                .then((res: any) => {
                  resetHouseholdTab();
                  fetchHousehold();
                  setShowToast(true);
                  setToastText("Member Deleted");
                  setToastBody(userToDelete);
                })
                .catch((err: any) => {
                  console.log(err);
                });
            })
            .catch((err: any) => {
              console.log(err);
            });
        }
      }
    };

    // reassign main contact
    if (type === "Guardian" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUserAndSaveNote())
        .catch((err: any) => {
          console.log(err);
          deleteUserAndSaveNote();
        });
    } else {
      deleteUserAndSaveNote();
    }
  };

  const onClickDeleteFinal = () => {
    setIsDeleting(false);
    setUserToDelete("");

    const deleteUser = () => {
      if (type === "Guardian") {
        deleteGuardian(id, userId)
          .then((res: any) => {
            resetHouseholdTab();
            fetchHousehold();
            setShowToast(true);
            setToastText("Guardian Deleted");
            setToastBody(userToDelete);
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else if (type === "Member") {
        deleteMember(id, userId)
          .then((res: any) => {
            resetHouseholdTab();
            fetchHousehold();
            setShowToast(true);
            setToastText("Member Deleted");
            setToastBody(userToDelete);
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    };

    // reassign main contact
    if (type === "Guardian" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUser())
        .catch((err: any) => {
          console.log(err);
          deleteUser();
        });
    } else {
      deleteUser();
    }
  };

  const onClickDeactivateCase = async () => {
    // Deactivate the case
    await patchCase({ casestatus: "inactive" }, id);

    // create exit note for the current user
    if (caseStatus === "active") {
      const note = {
        notetype: "exit",
        subject: {
          id: userId,
          type: type === "Guardian" ? "guardian" : "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };
      const res = await postNote(note, id);
      const noteId = res[0].data.note_id;

      const exitData = {
        exit_at: exitDate,
        exit_note_id: noteId,
      };

      if (type === "Guardian") {
        await patchGuardian(exitData, id, userId);
      } else {
        await patchMember(exitData, id, userId);
      }
    }

    setShowToast(true);
    setToastText("Case Deactivated");
    setToastBody(`Case has been made inactive`);
    fetchHousehold();
    resetHouseholdTab();
  };

  const updateExitNote = (e: any) => {
    setExitNote(e.target.value);
  };

  const updateExitType = (e: any) => {
    setExitType(e.target.value);
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setUserToDelete("");
  };

  return (
    <Card className="bg-light mb-3">
      <Card.Body>
        {isLastUser && caseStatus === "active" ? (
          <>
            <Stack direction="horizontal" gap={3} className="mb-3">
              <Stack className="justify-content-center">
                <Card.Subtitle>
                  You cannot delete the last person in a case. Would you like to change the case status to Inactive?
                </Card.Subtitle>
              </Stack>
              <Button className="app-danger-color button text-nowrap" onClick={onClickDeactivateCase}>
                Deactivate Case
              </Button>
              <div className="vr" />
              <Button className="app-primary-color button" onClick={onClickCancelDelete}>
                Cancel
              </Button>
            </Stack>
            <Row className="mb-3">
              <Col xs={12}>
                <FloatingLabel controlId="floatingInput" label="Exit Note">
                  <Form.Control
                    as="textarea"
                    type="textarea"
                    placeholder="Exit Note"
                    value={exitNote}
                    onChange={(e) => updateExitNote(e)}
                  />
                </FloatingLabel>
              </Col>
            </Row>
            <Row>
              {reassignMainContact && (
                <Col xs={4}>
                  <SelectObject
                    label="New Main Contact"
                    initialOption="None"
                    value={newMainContact}
                    options={mainContactOptions}
                    onUpdate={(e: any) => setNewMainContact(e.target.value)}
                  />
                </Col>
              )}
              <Col xs={reassignMainContact ? 4 : 8}>
                <SelectObject label="Exit Type" value={exitType} options={exitTypes} onUpdate={(e: any) => updateExitType(e)} />
              </Col>
              <Col xs={4}>
                <DatePicker label="Exit Date" currentValue={exitDate} setCurrentValue={setExitDate} hasMaxDate={true} />
              </Col>
            </Row>
          </>
        ) : isLastUser && caseStatus != "active" ? (
          <>
            <Stack direction="horizontal" gap={3} className="mb-3">
              <Stack style={{ justifyContent: "center" }}>
                <Card.Subtitle>You cannot delete the last member of a case.</Card.Subtitle>
              </Stack>
              <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
                Back
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Stack direction="horizontal" gap={3} className={caseStatus === "active" ? "mb-3" : ""}>
              <Stack style={{ justifyContent: "center" }}>
                <Card.Subtitle>Are you sure you want to delete {userToDelete}?</Card.Subtitle>
              </Stack>
              <Button
                className="app-danger-color button"
                onClick={() => {
                  caseStatus === "active" ? onClickDeleteFinalWExit() : onClickDeleteFinal();
                }}
              >
                Confirm Delete
              </Button>
              <div className="vr" />
              <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
                Cancel
              </Button>
            </Stack>
            {caseStatus === "active" && (
              <>
                <Row className="mb-3">
                  <Col xs={12}>
                    <FloatingLabel controlId="floatingInput" label="Exit Note">
                      <Form.Control
                        as="textarea"
                        type="textarea"
                        placeholder="Exit Note"
                        value={exitNote}
                        onChange={(e) => updateExitNote(e)}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  {reassignMainContact && (
                    <Col xs={4}>
                      <SelectObject
                        label="New Main Contact"
                        initialOption="None"
                        value={newMainContact}
                        options={mainContactOptions}
                        onUpdate={(e: any) => setNewMainContact(e.target.value)}
                      />
                    </Col>
                  )}
                  <Col xs={reassignMainContact ? 4 : 8}>
                    <SelectObject label="Exit Type" value={exitType} options={exitTypes} onUpdate={(e: any) => updateExitType(e)} />
                  </Col>
                  <Col xs={4}>
                    <DatePicker label="Exit Date" currentValue={exitDate} setCurrentValue={setExitDate} hasMaxDate={true} />
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ConfirmDelete;
